import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  keyframes,
  Typography,
  Collapse,
  IconButton,
  alpha,
  Checkbox,
} from "@mui/material";
import { StandardCard } from "../../../components/Cards";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatText } from "../../../helpers";
import {
  IUpsellPayload,
  IServiceCategoryCard,
  IVenueCard,
  IUpsellingServiceItem,
  IFoodCard,
  IUpsellingFoodItem,
} from "../types/upsell.types";
import { Flatware } from "@mui/icons-material";

// Keyframe animations
const blink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

interface IRenderUpsellServiceVenueCardProps {
  service?: IServiceCategoryCard | null;
  venue?: IVenueCard | null;
  food?: IFoodCard | null;
  handleUpsell?: (payload: IUpsellPayload) => void;
  isFromUpsellDashboard?: boolean;
  onClick?: () => void;
  isSelected?: boolean;
  upsellData?: IUpsellingServiceItem[];
  // Checkbox selection props
  isSelectable?: boolean;
  isChecked?: boolean;
  onCheckChange?: (
    uuid: string,
    type: "venue" | "service" | "food",
    checked: boolean,
    name?: string,
  ) => void;
  // View leads handler
  onViewLeads?: () => void;
  isForIndividualLead?: boolean;
  isMobileCompact?: boolean;
}

export const RenderUpsellServiceVenueCard = (
  props: IRenderUpsellServiceVenueCardProps,
) => {
  const {
    service,
    venue,
    food,
    handleUpsell,
    isFromUpsellDashboard,
    onClick,
    isSelected,
    upsellData,
    isSelectable = false,
    isChecked = false,
    onCheckChange,
    onViewLeads,
    isForIndividualLead = false,
    isMobileCompact = false,
  } = props;

  // State for expandable upsell items
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const isService = !!service;
  const isVenue = !!venue;
  const isFood = !!food;

  // Get UUID based on type
  const cardUuid = isService
    ? service?.service_category_uuid || ""
    : isVenue
      ? venue?.venue_uuid || ""
      : isFood
        ? food?.food_item_uuid || ""
        : "";

  // Get display values based on type
  const displayName = isService
    ? service?.service_category_name
    : isVenue
      ? venue?.venue_name || ""
      : isFood
        ? food?.item_name || ""
        : "--";
  // For venues, address is inside upselling_venue in leads
  const displayDescription = isService ? "" : "";

  // Handle checkbox change
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onCheckChange && cardUuid) {
      onCheckChange(
        cardUuid,
        isService ? "service" : isVenue ? "venue" : "food",
        e.target.checked,
        displayName || "",
      );
    }
  };

  // Check if card is clickable (has leads)
  const hasLeads =
    isFromUpsellDashboard &&
    (isService
      ? (service?.leads?.length ?? 0) > 0
      : isVenue
        ? (venue?.leads?.length ?? 0) > 0
        : (food?.leads?.length ?? 0) > 0);
  const isClickable = isSelectable && hasLeads;
  const leadsCount = isService
    ? service?.leads?.length || 0
    : isVenue
      ? venue?.leads?.length || 0
      : food?.leads?.length || 0;

  const handleCardClick = () => {
    // On card click, toggle the checkbox selection
    if (isSelectable && onCheckChange && cardUuid) {
      onCheckChange(
        cardUuid,
        isService ? "service" : isVenue ? "venue" : "food",
        !isChecked,
        displayName || "",
      );
    } else if (onClick) {
      onClick();
    }
  };

  const toggleExpand = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Get unique upsell items from leads for category - Only use this if upsellData is not provided
  const getUniqueUpsellItems = (): (IUpsellingServiceItem & {
    lead_count?: number;
  })[] => {
    // If upsellData is provided, use that instead
    if (upsellData && upsellData.length > 0) {
      return upsellData;
    }

    if (!isService || !service?.leads) return [];

    const upsellMap = new Map<
      string,
      IUpsellingServiceItem & { lead_count: number }
    >();

    service.leads.forEach((lead) => {
      // upselling_services is an array of service items within each lead
      const upsellingServices = lead.upselling_services;
      if (Array.isArray(upsellingServices)) {
        upsellingServices.forEach((serviceItem) => {
          if (serviceItem?.service_item_uuid) {
            const existing = upsellMap.get(serviceItem.service_item_uuid);
            if (!existing) {
              upsellMap.set(serviceItem.service_item_uuid, {
                ...serviceItem,
                lead_count: 1,
              });
            } else {
              existing.lead_count += 1;
            }
          }
        });
      }
    });
    return Array.from(upsellMap.values());
  };

  const allUpsellItems = getUniqueUpsellItems();

  // Get venue upsell info from leads - finds the first valid upselling_venue from all leads
  const getVenueUpsellInfo = () => {
    if (!isVenue || !venue?.leads) return null;

    for (const lead of venue.leads) {
      if (lead?.upselling_venue) {
        return lead.upselling_venue;
      }
    }
    return null;
  };

  const venueUpsellInfo = getVenueUpsellInfo();

  // Get unique upsell food items from leads for category - Only use this if upsellData is not provided
  const getUniqueUpsellFoodItems = (): (IUpsellingFoodItem & {
    lead_count?: number;
  })[] => {
    if (!isFood || !food?.leads) return [];

    const upsellMap = new Map<
      string,
      IUpsellingFoodItem & { lead_count: number }
    >();

    food.leads.forEach((lead) => {
      // upselling_food_item is an array of food items within each lead
      const upsellingFoodItems = lead.upselling_food_item;
      if (Array.isArray(upsellingFoodItems)) {
        upsellingFoodItems.forEach((foodItem) => {
          if (foodItem?.food_item_uuid) {
            const existing = upsellMap.get(foodItem.food_item_uuid);
            if (!existing) {
              upsellMap.set(foodItem.food_item_uuid, {
                ...foodItem,
                lead_count: 1,
              });
            } else {
              existing.lead_count += 1;
            }
          }
        });
      }
    });
    return Array.from(upsellMap.values());
  };

  const allUpsellFoodItems = getUniqueUpsellFoodItems();

  if (isMobileCompact) {
    return (
      <Box
        onClick={handleCardClick}
        sx={{
          width: "100%",
          minWidth: 0,
          height: "100%",
          cursor: isClickable
            ? "pointer"
            : hasLeads === false && isFromUpsellDashboard
              ? "not-allowed"
              : "default",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            width: "100%",
            height: "100%",
            minHeight: "185px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 1,
            borderRadius: "16px",
            backgroundColor: (theme: any) =>
              theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            border: (theme: any) =>
              isChecked
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
            boxShadow: (theme: any) =>
              isChecked
                ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`
                : "0 2px 10px rgba(0,0,0,0.03)",
            transition: "all 0.25s ease",
            position: "relative",
            overflow: "hidden",
            "&:active": isClickable
              ? {
                  transform: "scale(0.98)",
                }
              : {},
          }}
        >
          {/* Header Row: Category Icon + Title + Checkbox */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  backgroundColor: isService
                    ? "rgba(37, 99, 235, 0.1)"
                    : isVenue
                      ? "rgba(124, 58, 237, 0.1)"
                      : "rgba(16, 185, 129, 0.1)",
                }}
              >
                {isService ? (
                  <CategoryIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                ) : isVenue ? (
                  <LocationOnIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                ) : (
                  <Flatware sx={{ fontSize: 16, color: "#16a34a" }} />
                )}
              </Box>

              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  lineHeight: 1.2,
                  color: "text.primary",
                  minWidth: 0,
                }}
              >
                {formatText(displayName)}
              </Typography>
            </Box>

            {isSelectable && (
              <Checkbox
                checked={isChecked}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  p: 0.25,
                  color: (theme: any) =>
                    alpha(theme.palette.text.secondary, 0.4),
                  "&.Mui-checked": {
                    color: "primary.main",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 18,
                  },
                }}
              />
            )}
          </Box>

          {/* Middle Body Section: Leads Tag & Recommended Items Tag */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              my: 0.5,
              minWidth: 0,
            }}
          >
            {/* Leads Badge */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  py: 0.35,
                  px: 0.9,
                  borderRadius: "6px",
                  backgroundColor: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "rgba(37, 99, 235, 0.15)"
                      : "#eff6ff",
                }}
              >
                <PeopleAltIcon sx={{ fontSize: 13, color: "#2563eb" }} />
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: (theme: any) =>
                      theme.palette.mode === "dark" ? "#60a5fa" : "#1d4ed8",
                    lineHeight: 1,
                  }}
                >
                  {leadsCount} {leadsCount === 1 ? "Lead" : "Leads"}
                </Typography>
              </Box>
            </Box>

            {/* Recommended Upsells Accordion for Service */}
            {isService && allUpsellItems.length > 0 && (
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "rgba(16, 185, 129, 0.1)"
                      : "#f0fdf4",
                  border: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(16, 185, 129, 0.2)"
                      : "1px solid #dcfce7",
                  minWidth: 0,
                }}
              >
                {allUpsellItems.map((upsell: any, idx: number) => {
                  const isExpanded = expandedItems.has(idx);
                  const variants = Array.isArray(upsell.variant)
                    ? upsell.variant
                    : upsell.variant
                      ? [upsell.variant]
                      : [];

                  return (
                    <Box key={idx} sx={{ minWidth: 0 }}>
                      {/* Accordion Header - Click to toggle */}
                      <Box
                        onClick={(e) => toggleExpand(idx, e)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          minWidth: 0,
                          py: 0.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            gap: 0.5,
                          }}
                        >
                          <TrendingUpIcon
                            sx={{ fontSize: 14, color: "#16a34a", flexShrink: 0 }}
                          />
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.73rem",
                              fontWeight: 700,
                              color: (theme: any) =>
                                theme.palette.mode === "dark"
                                  ? "#34d399"
                                  : "#15803d",
                              lineHeight: 1.2,
                              minWidth: 0,
                            }}
                          >
                            {upsell.service_item_name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            color: "#16a34a",
                            p: 0.25,
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.25s ease",
                            flexShrink: 0,
                          }}
                        >
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {/* Accordion Expandable Content */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ pt: 0.5, pb: 0.25, pl: 0.5 }}>
                          {upsell.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.68rem",
                                display: "block",
                                mb: 0.5,
                                lineHeight: 1.35,
                              }}
                            >
                              {upsell.description}
                            </Typography>
                          )}

                          {variants.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              {variants.map((v: any, vIdx: number) => (
                                <Chip
                                  key={vIdx}
                                  label={`${v.type}: ₹${v.price}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: (theme: any) =>
                                      alpha(theme.palette.success.main, 0.12),
                                    color: "success.dark",
                                    fontWeight: 600,
                                    fontSize: "0.65rem",
                                    height: 20,
                                    border: (theme: any) =>
                                      `1px solid ${alpha(
                                        theme.palette.success.main,
                                        0.3,
                                      )}`,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Recommended Upsells Accordion for Food */}
            {isFood && allUpsellFoodItems.length > 0 && (
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "rgba(16, 185, 129, 0.1)"
                      : "#f0fdf4",
                  border: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(16, 185, 129, 0.2)"
                      : "1px solid #dcfce7",
                  minWidth: 0,
                }}
              >
                {allUpsellFoodItems.map((upsell: any, idx: number) => {
                  const isExpanded = expandedItems.has(idx);
                  const variants = Array.isArray(upsell.sub_category_list)
                    ? upsell.sub_category_list
                    : upsell.sub_category_list
                      ? [upsell.sub_category_list]
                      : [];

                  return (
                    <Box key={idx} sx={{ minWidth: 0 }}>
                      {/* Accordion Header - Click to toggle */}
                      <Box
                        onClick={(e) => toggleExpand(idx, e)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          minWidth: 0,
                          py: 0.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            gap: 0.5,
                          }}
                        >
                          <TrendingUpIcon
                            sx={{ fontSize: 14, color: "#16a34a", flexShrink: 0 }}
                          />
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.73rem",
                              fontWeight: 700,
                              color: (theme: any) =>
                                theme.palette.mode === "dark"
                                  ? "#34d399"
                                  : "#15803d",
                              lineHeight: 1.2,
                              minWidth: 0,
                            }}
                          >
                            {upsell.item_name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            color: "#16a34a",
                            p: 0.25,
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.25s ease",
                            flexShrink: 0,
                          }}
                        >
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {/* Accordion Expandable Content */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ pt: 0.5, pb: 0.25, pl: 0.5 }}>
                          {upsell.item_description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.68rem",
                                display: "block",
                                mb: 0.5,
                                lineHeight: 1.35,
                              }}
                            >
                              {upsell.item_description}
                            </Typography>
                          )}

                          {variants.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              {variants.map((v: any, vIdx: number) => (
                                <Chip
                                  key={vIdx}
                                  label={`${v.subcategory_name}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: (theme: any) =>
                                      alpha(theme.palette.success.main, 0.12),
                                    color: "success.dark",
                                    fontWeight: 600,
                                    fontSize: "0.65rem",
                                    height: 20,
                                    border: (theme: any) =>
                                      `1px solid ${alpha(
                                        theme.palette.success.main,
                                        0.3,
                                      )}`,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Recommended Capacities Accordion for Venue */}
            {isVenue && venueUpsellInfo && (
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "rgba(124, 58, 237, 0.1)"
                      : "#f5f3ff",
                  border: (theme: any) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(124, 58, 237, 0.2)"
                      : "1px solid #ede9fe",
                  minWidth: 0,
                }}
              >
                <Box
                  onClick={(e) => toggleExpand(0, e)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    minWidth: 0,
                    py: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#7c3aed",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Capacities
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      color: "#7c3aed",
                      p: 0.25,
                      transform: expandedItems.has(0)
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                      flexShrink: 0,
                    }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Collapse in={expandedItems.has(0)}>
                  <Box
                    sx={{
                      pt: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: (theme: any) =>
                          theme.palette.mode === "dark" ? "#a78bfa" : "#6d28d9",
                      }}
                    >
                      Seating: {venueUpsellInfo.seating_capacity?.toLocaleString() || "N/A"} guests
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: (theme: any) =>
                          theme.palette.mode === "dark" ? "#a78bfa" : "#6d28d9",
                      }}
                    >
                      Floating: {venueUpsellInfo.floating_capacity?.toLocaleString() || "N/A"} guests
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>

          {/* Bottom Row: View Leads Action Button */}
          {isFromUpsellDashboard && hasLeads && onViewLeads && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onViewLeads();
              }}
              sx={{
                py: 0.6,
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "none",
                lineHeight: 1.2,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
                },
              }}
            >
              View Leads
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        width: isMobileCompact ? "100%" : "auto",
        minWidth: 0,
        maxWidth: "100%",
        cursor: isClickable
          ? "pointer"
          : hasLeads === false && isFromUpsellDashboard
            ? "not-allowed"
            : "default",
      }}
    >
      <StandardCard
        sx={{
          p: 0,
          height: isMobileCompact ? "100%" : "420px",
          minHeight: isMobileCompact ? "230px" : "auto",
          width: isMobileCompact ? "100%" : "310px",
          minWidth: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          borderRadius: isMobileCompact ? "12px" : 3,
          boxShadow: (theme: any) =>
            isSelected
              ? `0 8px 28px ${alpha(theme.palette.primary.main, 0.3)}`
              : `0 3px 16px ${alpha(theme.palette.grey[500], 0.12)}`,
          background: (theme: any) => theme.palette.background.paper,
          border: (theme: any) =>
            isSelected
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": isClickable
            ? {
                // transform: "translateY(-4px)",
                boxShadow: (theme: any) =>
                  `0 12px 36px ${alpha(theme.palette.primary.main, 0.22)}`,
                border: (theme: any) =>
                  `2px solid ${theme.palette.primary.main}`,
              }
            : {},
        }}
      >
        {/* ===== HEADER SECTION - Primary Color Background (Fixed) ===== */}
        <Box
          sx={{
            p: isMobileCompact ? 0.75 : 1.75,
            pb: isMobileCompact ? 0.75 : 1.75,
            backgroundColor: "primary.main",
            borderRadius: isMobileCompact ? "10px 10px 0 0" : 2,
            position: "relative",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {/* Checkbox for selection - Top Right */}
          {isSelectable && (
            <Box
              sx={{
                position: "absolute",
                top: isMobileCompact ? 2 : 6,
                right: isMobileCompact ? 2 : 6,
                zIndex: 2,
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-checked": {
                    color: "white",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: isMobileCompact ? 16 : 22,
                  },
                  p: 0.25,
                  backgroundColor: isChecked
                    ? "rgba(255, 255, 255, 0.2)"
                    : "transparent",
                  borderRadius: 0.75,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  },
                }}
              />
            </Box>
          )}

          {/* Type Indicator with Blinking Animation + Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobileCompact ? 0.5 : 1.5,
              pr: isSelectable ? (isMobileCompact ? 2.5 : 4) : 0, // Add padding if checkbox is present
              minWidth: 0,
            }}
          >
            {/* Blinking Type Indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: isMobileCompact ? 22 : 32,
                height: isMobileCompact ? 22 : 32,
                borderRadius: 1.2,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(8px)",
                animation: `${blink} 2.5s ease-in-out infinite`,
                flexShrink: 0,
              }}
            >
              {isService ? (
                <CategoryIcon
                  sx={{
                    fontSize: isMobileCompact ? 13 : 18,
                    color: "white",
                  }}
                />
              ) : isVenue ? (
                <LocationOnIcon
                  sx={{
                    fontSize: isMobileCompact ? 13 : 18,
                    color: "white",
                  }}
                />
              ) : isFood ? (
                <Flatware
                  sx={{
                    fontSize: isMobileCompact ? 13 : 18,
                    color: "white",
                  }}
                />
              ) : (
                <></>
              )}
            </Box>

            {/* Category/Venue Name */}
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: "white",
                fontSize: isMobileCompact ? "0.75rem" : "1.1rem",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: isMobileCompact ? "nowrap" : "normal",
                display: isMobileCompact ? "block" : "-webkit-box",
                WebkitLineClamp: isMobileCompact ? undefined : 2,
                WebkitBoxOrient: isMobileCompact ? undefined : "vertical",
                minWidth: 0,
              }}
            >
              {formatText(displayName)}
            </Typography>
          </Box>
        </Box>

        {/* ===== CARD BODY ===== */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            p: isMobileCompact ? 0.5 : 0,
            pt: isMobileCompact ? 0.5 : 0,
            overflow: "hidden",
          }}
        >
          {/* Fixed Section: Leads Count & Description */}
          <Box
            sx={{
              p: isMobileCompact ? 0 : 1.75,
              pt: isMobileCompact ? 0.5 : 1.5,
              pb: isMobileCompact ? 0.5 : 0,
              px: isMobileCompact ? 0.5 : 0,
              flexShrink: 0,
            }}
          >
            {/* Leads Count Chip */}
            {!isForIndividualLead && (
              <Box sx={{ mb: isMobileCompact ? 0.5 : 1.5 }}>
                <Chip
                  label={
                    isMobileCompact
                      ? `Leads : ${leadsCount}`
                      : `Leads Count : ${leadsCount}`
                  }
                  size="small"
                  sx={{
                    backgroundColor: (theme: any) =>
                      alpha(theme.palette.primary.main, 0.08),
                    borderRadius: isMobileCompact ? 1.5 : 2.5,
                    border: (theme: any) =>
                      `1.5px solid ${theme.palette.primary.main}`,
                    color: "primary.main",
                    fontWeight: 600,
                    height: isMobileCompact ? 20 : 28,
                    fontSize: isMobileCompact ? "0.65rem" : "0.8rem",
                    "& .MuiChip-label": { px: isMobileCompact ? 0.75 : 1.5 },
                  }}
                />
              </Box>
            )}

            {/* Description - Category Description or Venue Address */}
            {displayDescription && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 1.5,
                  lineHeight: 1.5,
                  fontSize: "0.8rem",
                  fontWeight: 500,
                }}
              >
                {displayDescription}
              </Typography>
            )}
          </Box>

          {/* Scrollable/Centered Section for Venue Options or Recommended Upsells */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: isVenue ? "center" : "flex-start",
              px: isMobileCompact ? 0.5 : 1.75,
              pb: isMobileCompact ? 0.5 : 1.5,
              minHeight: 0,
              overflowY: isService ? "auto" : "visible",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: (theme: any) => alpha(theme.palette.grey[200], 0.5),
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: (theme: any) => alpha(theme.palette.grey[400], 0.8),
                borderRadius: "3px",
                "&:hover": {
                  background: (theme: any) =>
                    alpha(theme.palette.grey[500], 0.8),
                },
              },
            }}
          >
            {/* ===== VENUE: Floating and Seating Capacities (Centered) ===== */}
            {isVenue && (
              <Box
                sx={{
                  p: isMobileCompact ? 0.75 : 1.5,
                  background: (theme: any) =>
                    `linear-gradient(145deg, ${alpha(
                      theme.palette.primary.light,
                      0.3,
                    )} 0%, ${alpha(theme.palette.grey[100], 0.8)} 100%)`,
                  borderRadius: isMobileCompact ? 1.5 : 2.5,
                  border: (theme: any) =>
                    `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: isMobileCompact ? 0.5 : 1,
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      fontSize: isMobileCompact ? 13 : 15,
                      mr: 0.5,
                      color: "primary.main",
                    }}
                  />
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: "text.primary",
                      fontSize: isMobileCompact ? "0.68rem" : "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    {isMobileCompact
                      ? "Venue Capacities"
                      : "Venue Upsell Available Options"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isMobileCompact ? 0.5 : 0.75,
                  }}
                >
                  {/* Floating Capacity */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: isMobileCompact ? 0.5 : 1,
                      backgroundColor: "background.paper",
                      borderRadius: 1.5,
                      border: (theme: any) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <PeopleAltIcon
                        sx={{
                          fontSize: isMobileCompact ? 13 : 15,
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: isMobileCompact ? "0.65rem" : "0.75rem",
                        }}
                      >
                        {isMobileCompact ? "Floating" : "Floating Capacity"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: isMobileCompact ? "0.65rem" : "0.75rem",
                        ml: 0.5,
                      }}
                    >
                      {venueUpsellInfo?.floating_capacity?.toLocaleString() ||
                        "N/A"}
                    </Typography>
                  </Box>

                  {/* Seating Capacity */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: isMobileCompact ? 0.5 : 1,
                      backgroundColor: "background.paper",
                      borderRadius: 1.5,
                      border: (theme: any) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <EventSeatIcon
                        sx={{
                          fontSize: isMobileCompact ? 13 : 15,
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          fontSize: isMobileCompact ? "0.65rem" : "0.75rem",
                        }}
                      >
                        {isMobileCompact ? "Seating" : "Seating Capacity"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: isMobileCompact ? "0.65rem" : "0.75rem",
                        ml: 0.5,
                      }}
                    >
                      {venueUpsellInfo?.seating_capacity?.toLocaleString() ||
                        "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ===== CATEGORY: Recommended Upsells Section (Scrollable) ===== */}
            {isService && allUpsellItems.length > 0 && (
              <Box
                sx={{
                  p: isMobileCompact ? 0.75 : 1.5,
                  background: (theme: any) =>
                    `linear-gradient(145deg, ${alpha(
                      theme.palette.success.light,
                      0.25,
                    )} 0%, ${alpha(theme.palette.grey[50], 0.9)} 100%)`,
                  borderRadius: isMobileCompact ? 1.5 : 2.5,
                  border: (theme: any) =>
                    `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: isMobileCompact ? 0.5 : 1,
                    gap: 0.5,
                    flexWrap: isMobileCompact ? "wrap" : "nowrap",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  >
                    <TrendingUpIcon
                      sx={{
                        fontSize: isMobileCompact ? 13 : 15,
                        mr: 0.5,
                        color: "success.main",
                        animation: `${pulse} 2s ease-in-out infinite`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.primary",
                        fontSize: isMobileCompact ? "0.68rem" : "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      Recommended
                    </Typography>
                  </Box>
                  <Chip
                    label={`${allUpsellItems.length} ${isMobileCompact ? "Svcs" : "Services"}`}
                    size="small"
                    sx={{
                      backgroundColor: "success.main",
                      color: "white",
                      fontWeight: 600,
                      height: isMobileCompact ? 18 : 22,
                      fontSize: isMobileCompact ? "0.6rem" : "0.7rem",
                      "& .MuiChip-label": { px: isMobileCompact ? 0.5 : 1 },
                    }}
                  />
                </Box>

                {/* Expandable Upsell Items */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {allUpsellItems.map((upsell: any, idx: number) => {
                    const isExpanded = expandedItems.has(idx);
                    const variants = Array.isArray(upsell.variant)
                      ? upsell.variant
                      : upsell.variant
                        ? [upsell.variant]
                        : [];

                    return (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "background.paper",
                          borderRadius: 1.5,
                          border: (theme: any) =>
                            `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          overflow: "hidden",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: (theme: any) =>
                              alpha(theme.palette.success.main, 0.4),
                          },
                        }}
                      >
                        {/* Collapsed Header - Service Name with Arrow */}
                        <Box
                          onClick={(e) => toggleExpand(idx, e)}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: isMobileCompact ? 0.5 : 1,
                            cursor: "pointer",
                            minWidth: 0,
                            "&:hover": {
                              backgroundColor: (theme: any) =>
                                alpha(theme.palette.grey[100], 0.5),
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: "text.primary",
                              fontWeight: 600,
                              fontSize: isMobileCompact ? "0.68rem" : "0.75rem",
                              borderBottom: (theme: any) =>
                                `1.5px solid ${theme.palette.warning.main}`,
                              pb: 0.25,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {upsell.service_item_name}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              color: "text.secondary",
                              p: 0.25,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.3s ease",
                              flexShrink: 0,
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: isMobileCompact ? 15 : 18 }}
                            />
                          </IconButton>
                        </Box>

                        {/* Expandable Content */}
                        <Collapse in={isExpanded}>
                          <Box
                            sx={{
                              px: 1,
                              pb: 1,
                              borderTop: (theme: any) =>
                                `1px solid ${alpha(
                                  theme.palette.grey[200],
                                  0.8,
                                )}`,
                              backgroundColor: (theme: any) =>
                                alpha(theme.palette.grey[50], 0.5),
                            }}
                          >
                            {/* Description */}
                            {upsell.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.7rem",
                                  display: "block",
                                  mt: 1,
                                  mb: 1,
                                  lineHeight: 1.4,
                                }}
                              >
                                {upsell.description}
                              </Typography>
                            )}

                            {/* Variants */}
                            {variants.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {variants.map((v: any, vIdx: number) => (
                                  <Chip
                                    key={vIdx}
                                    label={`${v.type}: ₹${v.price}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: (theme: any) =>
                                        alpha(theme.palette.success.main, 0.1),
                                      color: "success.dark",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 22,
                                      border: (theme: any) =>
                                        `1px solid ${alpha(
                                          theme.palette.success.main,
                                          0.3,
                                        )}`,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ===== FOOD: Recommended Upsells Section (Scrollable) ===== */}
            {isFood && allUpsellFoodItems.length > 0 && (
              <Box
                sx={{
                  p: isMobileCompact ? 0.75 : 1.5,
                  background: (theme: any) =>
                    `linear-gradient(145deg, ${alpha(
                      theme.palette.success.light,
                      0.25,
                    )} 0%, ${alpha(theme.palette.grey[50], 0.9)} 100%)`,
                  borderRadius: isMobileCompact ? 1.5 : 2.5,
                  border: (theme: any) =>
                    `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: isMobileCompact ? 0.5 : 1,
                    gap: 0.5,
                    flexWrap: isMobileCompact ? "wrap" : "nowrap",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  >
                    <TrendingUpIcon
                      sx={{
                        fontSize: isMobileCompact ? 13 : 15,
                        mr: 0.5,
                        color: "success.main",
                        animation: `${pulse} 2s ease-in-out infinite`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.primary",
                        fontSize: isMobileCompact ? "0.68rem" : "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      Recommended
                    </Typography>
                  </Box>
                  <Chip
                    label={`${allUpsellFoodItems.length} ${isMobileCompact ? "Items" : "Food Items"}`}
                    size="small"
                    sx={{
                      backgroundColor: "success.main",
                      color: "white",
                      fontWeight: 600,
                      height: isMobileCompact ? 18 : 22,
                      fontSize: isMobileCompact ? "0.6rem" : "0.7rem",
                      "& .MuiChip-label": { px: isMobileCompact ? 0.5 : 1 },
                    }}
                  />
                </Box>

                {/* Expandable Upsell Items */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {allUpsellFoodItems.map((upsell: any, idx: number) => {
                    const isExpanded = expandedItems.has(idx);
                    const variants = Array.isArray(upsell.sub_category_list)
                      ? upsell.sub_category_list
                      : upsell.sub_category_list
                        ? [upsell.sub_category_list]
                        : [];

                    return (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "background.paper",
                          borderRadius: 1.5,
                          border: (theme: any) =>
                            `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          overflow: "hidden",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: (theme: any) =>
                              alpha(theme.palette.success.main, 0.4),
                          },
                        }}
                      >
                        {/* Collapsed Header - Service Name with Arrow */}
                        <Box
                          onClick={(e) => toggleExpand(idx, e)}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: isMobileCompact ? 0.5 : 1,
                            cursor: "pointer",
                            minWidth: 0,
                            "&:hover": {
                              backgroundColor: (theme: any) =>
                                alpha(theme.palette.grey[100], 0.5),
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: "text.primary",
                              fontWeight: 600,
                              fontSize: isMobileCompact ? "0.68rem" : "0.75rem",
                              borderBottom: (theme: any) =>
                                `1.5px solid ${theme.palette.warning.main}`,
                              pb: 0.25,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {upsell.item_name}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              color: "text.secondary",
                              p: 0.25,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.3s ease",
                              flexShrink: 0,
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: isMobileCompact ? 15 : 18 }}
                            />
                          </IconButton>
                        </Box>

                        {/* Expandable Content */}
                        <Collapse in={isExpanded}>
                          <Box
                            sx={{
                              px: 1,
                              pb: 1,
                              borderTop: (theme: any) =>
                                `1px solid ${alpha(
                                  theme.palette.grey[200],
                                  0.8,
                                )}`,
                              backgroundColor: (theme: any) =>
                                alpha(theme.palette.grey[50], 0.5),
                            }}
                          >
                            {/* Description */}
                            {upsell.item_description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.7rem",
                                  display: "block",
                                  mt: 1,
                                  mb: 1,
                                  lineHeight: 1.4,
                                }}
                              >
                                {upsell.item_description}
                              </Typography>
                            )}

                            {/* Variants */}
                            {variants.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {variants.map((v: any, vIdx: number) => (
                                  <Chip
                                    key={vIdx}
                                    label={`${v.subcategory_name}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: (theme: any) =>
                                        alpha(theme.palette.success.main, 0.1),
                                      color: "success.dark",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 22,
                                      border: (theme: any) =>
                                        `1px solid ${alpha(
                                          theme.palette.success.main,
                                          0.3,
                                        )}`,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ===== ACTION BUTTON - Only show when handleUpsell is provided ===== */}
        {/* {handleUpsell && (
          <Box sx={{ p: 1.75, pt: 0 }}>
            <Button
              variant="contained"
              fullWidth
              endIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
              disabled={hasLeads === false && isFromUpsellDashboard}
              onClick={(e) => {
                e.stopPropagation();
                handleUpsell({
                  company_outlet_uuid:
                    companyOutlet.comapny.company_outlet_uuid,
                  service_templates: isService ? 1 : 0,
                  venue_templates: isVenue ? 1 : 0,
                });
              }}
              sx={{
                py: 1,
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: 2.5,
                backgroundColor: "primary",
                boxShadow: (theme: any) =>
                  `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "primary.main",
                  boxShadow: (theme: any) =>
                    `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
                "&:disabled": {
                  background: (theme: any) => theme.palette.grey[300],
                  color: (theme: any) => theme.palette.grey[500],
                },
              }}
            >
              View Recommended Templates
            </Button>
          </Box>
        )} */}

        {/* ===== VIEW UPSELL LEADS BUTTON - Only show in dashboard with leads (Fixed) ===== */}
        {isFromUpsellDashboard && hasLeads && onViewLeads && (
          <Box
            sx={{
              p: isMobileCompact ? 0.5 : 1.75,
              pt: isMobileCompact ? 0.25 : 1,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              startIcon={
                <VisibilityIcon sx={{ fontSize: isMobileCompact ? 13 : 18 }} />
              }
              onClick={(e) => {
                e.stopPropagation();
                onViewLeads();
              }}
              sx={{
                py: isMobileCompact ? 0.4 : 1,
                px: isMobileCompact ? 0.4 : 1.5,
                fontWeight: 700,
                fontSize: isMobileCompact ? "0.68rem" : "0.85rem",
                borderRadius: isMobileCompact ? 1.5 : 2.5,
                borderWidth: 2,
                borderColor: "primary.main",
                color: "#fff",
                transition: "all 0.3s ease",
                textTransform: "none",
                lineHeight: 1.2,
                minHeight: isMobileCompact ? 26 : "auto",
                "& .MuiButton-startIcon": {
                  mr: isMobileCompact ? 0.4 : 1,
                  ml: isMobileCompact ? -0.2 : 0,
                },
              }}
            >
              {isMobileCompact ? "View Leads" : "View Upsell Leads"}
            </Button>
          </Box>
        )}
      </StandardCard>
    </Box>
  );
};
